// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { WEIGHT_STEPS } from '@/components/GameWeightControl';
import { DEFAULT_GAME_WEIGHT_PERCENT } from '@/features/game-selection/weights';
import { TOURNAMENT_ONLY_GAME_IDS, isTournamentOnlyGameId } from '@/features/game-definitions/tournamentOnly';
import { ensureTournamentGamesSelected } from '@/services/tournamentGameSelection';
import type { RecentRoundGameSetup } from '@/features/round-init/types';

const MAX_WEIGHT_PERCENT = Math.max(...(WEIGHT_STEPS as readonly number[]));

/**
 * Keeps a stored weight inside the ladder's range. Deliberately not `normalizeWeight`, which
 * caps at 100 and would quietly demote a game the user had set to 150 %.
 */
function sanitizeCopiedWeight(value: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_GAME_WEIGHT_PERCENT;
  }
  return Math.max(0, Math.min(MAX_WEIGHT_PERCENT, Math.round(value)));
}

export type CopiedGameSetup = {
  selectedGameIds: Set<string>;
  weightsByGameId: Record<string, number>;
  /**
   * Sparse, unlike the weights: only games the source round pinned a rule set for get an entry.
   * Ids the target game no longer offers are the caller's to drop — see sanitizeRuleSetSelection,
   * which needs the definitions this module deliberately does not take.
   */
  ruleSetByGameId: Record<string, string>;
  /** Games in the source round that could not be copied, so callers can log it. */
  skippedGameIds: string[];
};

/**
 * Replaces the current game selection with a previous round's, keeping the tour's own
 * tournament games selected regardless. Games the catalogue no longer offers, and tour-only
 * composites (Eclectic, Freeze) that never belong to a single round, are dropped. Weights of
 * tournament games are left alone — those are tour-managed and not the user's to set here.
 */
export function applyCopiedGameSetup(params: {
  setup: RecentRoundGameSetup;
  /** Ids of the game definitions currently offered on the screen. */
  knownGameIds: Iterable<string>;
  tournamentGameIds: Iterable<string>;
  currentWeights: Record<string, number>;
  currentRuleSets?: Record<string, string>;
}): CopiedGameSetup {
  const { setup, currentWeights, currentRuleSets } = params;
  const knownGameIds = new Set(params.knownGameIds);
  const tournamentGameIds = Array.from(params.tournamentGameIds);
  const tournamentGameIdSet = new Set(tournamentGameIds);

  const copiedIds: string[] = [];
  const skippedGameIds: string[] = [];
  setup.games.forEach((game) => {
    if (!knownGameIds.has(game.gameDefinitionId) || isTournamentOnlyGameId(game.gameDefinitionId)) {
      skippedGameIds.push(game.gameDefinitionId);
      return;
    }
    copiedIds.push(game.gameDefinitionId);
  });

  const weightsByGameId = { ...currentWeights };
  setup.games.forEach((game) => {
    if (!copiedIds.includes(game.gameDefinitionId) || tournamentGameIdSet.has(game.gameDefinitionId)) {
      return;
    }
    weightsByGameId[game.gameDefinitionId] = sanitizeCopiedWeight(game.weightPercent);
  });

  // Rule sets are copied for tournament games too, unlike the weights: a tour game's weight is
  // tour-managed, but its rule set is only a tour *default* that a round may override.
  const ruleSetByGameId = { ...(currentRuleSets ?? {}) };
  setup.games.forEach((game) => {
    if (!copiedIds.includes(game.gameDefinitionId)) {
      return;
    }
    if (typeof game.ruleSetId === 'string' && game.ruleSetId.length > 0) {
      ruleSetByGameId[game.gameDefinitionId] = game.ruleSetId;
    }
  });

  return {
    selectedGameIds: ensureTournamentGamesSelected(copiedIds, tournamentGameIds, TOURNAMENT_ONLY_GAME_IDS),
    weightsByGameId,
    ruleSetByGameId,
    skippedGameIds,
  };
}
